import { stripTypename } from "@apollo/client/utilities";
import type { EventInput } from "@fullcalendar/core/index.js";
import {
  ActionPayloadType,
  ScheduledEventModificationType,
  type ActionPayload,
  type CreateScheduledEventMutationVariables,
  type ScheduledEvent,
  type UpdateScheduledEventMutationVariables,
} from "@package/ui-gql/index-data-access";
import { Frequency } from "rrule";

// Shared with SchedulerEdit.tsx and SchedulerEventSummary.tsx -- computed
// once at module load rather than separately in each (the value can't change
// within a session without a page reload).
export const BROWSER_TIMEZONE =
  Intl.DateTimeFormat().resolvedOptions().timeZone;

export type SchedulerEventTone =
  | "profile"
  | "post-enable"
  | "post-disable"
  | "neutral";

export type SchedulerEventExtendedProps = {
  tone: SchedulerEventTone;
  timezone?: string | null;
  recurring: boolean;
};

const TONE_CLASS_NAMES: Record<SchedulerEventTone, string[]> = {
  profile: ["scheduler-event", "scheduler-event--profile"],
  "post-enable": ["scheduler-event", "scheduler-event--post-enable"],
  "post-disable": ["scheduler-event", "scheduler-event--post-disable"],
  neutral: ["scheduler-event", "scheduler-event--neutral"],
};

export function getSchedulerEventTone(
  actions?: (ActionPayload | null | undefined)[] | null,
): SchedulerEventTone {
  const profileChange = actions?.find(
    (action) => action?.type === ActionPayloadType.ActionLocationProfileChanged,
  );
  if (profileChange) {
    return "profile";
  }

  const postAction = actions?.find(
    (action) => action?.type === ActionPayloadType.ActionPostEnabled,
  );
  if (postAction && "enabled" in postAction) {
    return postAction.enabled ? "post-enable" : "post-disable";
  }

  return "neutral";
}

export function getSchedulerEventClassNames(
  tone: SchedulerEventTone,
  selected?: boolean,
): string[] {
  return selected
    ? [...TONE_CLASS_NAMES[tone], "scheduler-event--selected"]
    : TONE_CLASS_NAMES[tone];
}

export function convertScheduledEventsToCalendarInputs(
  events?: ScheduledEvent[],
  selectedEventId?: string,
): EventInput[] {
  if (!Array.isArray(events)) {
    return [];
  }

  return events.map((event) => {
    const tone = getSchedulerEventTone(
      event.startAction?.length ? event.startAction : event.endAction,
    );
    const extendedProps: SchedulerEventExtendedProps = {
      tone,
      timezone: event.timezone,
      recurring: !!event.recurring,
    };

    return {
      id: event.id ?? undefined,
      // No `rrule` here -- the backend materializes recurrence as one real
      // document per occurrence (each with its own concrete start/end), and
      // every one of those documents carries the series' `recurring`
      // settings as metadata (scheduled-event.service.ts). Attaching an
      // `rrule` built from that metadata to *each* materialized occurrence
      // told @fullcalendar/rrule to independently re-expand the *entire*
      // series from every single occurrence, so a 5-occurrence series
      // rendered as 5 superimposed virtual series landing on the same 5
      // days -- "many of the same event on the same day". `extendedProps`
      // still exposes `recurring` as a boolean for the repeat-icon chip.
      start: new Date(event.start),
      end: new Date(event.end),
      groupId: event.groupId ?? undefined,
      title: event.name ?? undefined,
      classNames: getSchedulerEventClassNames(
        tone,
        selectedEventId === event.id,
      ),
      extendedProps,
    };
  });
}

export function createScheduledEventMutationVariables(
  event: ScheduledEvent,
): CreateScheduledEventMutationVariables {
  return {
    ...stripTypename(event),
    ...adjustScheduledEventStartEndActions(event),
    name: event.name ?? "Not Set",
    start: event.start,
    recurring: event.recurring
      ? {
          ...event.recurring,
          freq: event.recurring.freq ?? Frequency.DAILY,
          interval: event.recurring.interval ?? 1,
          // The editor only ever seeds dtstart/tzid once, when Repeat is
          // first turned on (SchedulerRecurrenceEditor.updateRecurring's
          // defaults) -- a later change to the event's own Start or
          // Timezone would otherwise never reach the recurrence rule. The
          // event's own fields are the single source of truth here.
          dtstart: event.start
            ? new Date(event.start).toISOString()
            : event.recurring.dtstart,
          tzid: event.timezone ?? event.recurring.tzid,
          // No client-side count fallback: an unbounded ("Never") rule is
          // sent as-is and the service clamps to MAX_RECURRING_OCCURRENCES
          // itself (scheduled-event.service.ts) -- one place enforces the
          // cap instead of two agreeing by coincidence.
          count: event.recurring.count,
        }
      : undefined,
  };
}

export function updateScheduledEventMutationVariables(
  event: ScheduledEvent,
  modifyType: ScheduledEventModificationType = ScheduledEventModificationType.Single,
): UpdateScheduledEventMutationVariables {
  return {
    ...stripTypename(event),
    ...adjustScheduledEventStartEndActions(event),
    id: event.id ?? "",
    name: event.name ?? "Not Set",
    modifyType,
    start: event.start,
  };
}

function adjustScheduledEventStartEndActions(
  event: ScheduledEvent,
):
  | Partial<CreateScheduledEventMutationVariables>
  | Partial<UpdateScheduledEventMutationVariables> {
  return {
    startActionOneOf:
      event.startAction?.map((action) => ({
        type: action.type,
        oneOf: {
          [action.type]: stripTypename({
            ...action,
            type: undefined,
          }),
        },
      })) ?? undefined,
    endActionOneOf:
      event.endAction?.map((action) => ({
        type: action.type,
        oneOf: {
          [action.type]: stripTypename({
            ...action,
            type: undefined,
          }),
        },
      })) ?? undefined,
  };
}
